/**
 * How a repository reaches an environment: the one schema a deploy source is
 * written in. The store, the routes, Harper's gather and the Repositories page
 * all read this shape, and the type is inferred from it, so a field added
 * here reaches every reader without a second interface to keep in step.
 *
 * A source is one mechanism (`kind`) with its own config. The stored `kind`
 * column carries no CHECK: a new mechanism is a new member of the union here,
 * not a migration. A stored row whose kind this build does not know fails to
 * parse and is skipped by readers, never guessed at.
 *
 * This module runs in the browser too, so it uses nothing from Node.
 */
import { z } from "zod";
export const DEPLOY_SOURCE_ENVIRONMENTS = ["production", "staging"];
/** A workflow file under `.github/workflows`, as GitHub names it in its API. */
const workflowPath = z
    .string()
    .trim()
    .max(300)
    .regex(/^\.github\/workflows\/[^/\s]+\.ya?ml$/, "Name a workflow file under .github/workflows");
/**
 * A job's name exactly as GitHub's jobs API reports it: the job's `name:`, or
 * its key when it has none, and `<caller> / <callee job>` for a job that calls
 * a reusable workflow.
 */
const jobName = z.string().trim().min(1, "Name the job that deploys").max(300);
/** A GitHub Actions workflow whose named job succeeding is the deploy. */
export const actionsWorkflowDeploySourceSchema = z
    .object({
    kind: z.literal("actions_workflow"),
    workflowPath,
    jobName,
})
    .strict();
const mechanism = z.discriminatedUnion("kind", [actionsWorkflowDeploySourceSchema]);
/** A service name inside a monorepo ("web", "api"); absent for a whole-repo deploy. */
const label = z
    .string()
    .trim()
    .max(60)
    .transform((value) => value || null)
    .nullable()
    .default(null);
export const deploySourceSchema = z
    .object({
    environment: z.enum(DEPLOY_SOURCE_ENVIRONMENTS).default("production"),
    label,
    source: mechanism,
})
    .strict();
/** Every source of one repository: one per environment and label. */
export const deploySourceListSchema = z
    .array(deploySourceSchema)
    .max(20)
    .refine((sources) => new Set(sources.map((s) => `${s.environment}\u0000${s.label ?? ""}`)).size === sources.length, "One source per environment and service");
//# sourceMappingURL=deploySourceSchema.js.map